import React, { useEffect, useState } from "react";
import { Loader2, Save, BookOpen, Trash2, Edit, Plus, RefreshCw, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getAllExams, upsertExam, deleteExam } from "../../api/axios";

// Import your unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterFormCard,
  masterFormFieldGroup,
  masterInputLabel,
  masterInput,
  masterSelect,
  masterFormBtnSubmit,
  masterBtnBack,
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const ExamMaster = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Determine initial view based on URL path (/new, /edit/:id, or root list/form)
  const isNew = location.pathname.endsWith("/new");
  const isEdit = Boolean(id) || location.pathname.includes("/edit");

  // View mode state: default to "form" for new/edit or handle switching
  const [viewMode, setViewMode] = useState(isNew || isEdit ? "form" : "form");

  const [editId, setEditId] = useState(id || null);
  const [exams, setExams] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // List filter and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    examCode: "[AUTO GENERATED]",
    examName: "",
    status: "ACTIVE",
  });

  const fetchExams = async () => {
    setLoading(true);
    try {
      const res = await getAllExams();
      const examList = res.data || [];
      setExams(examList);

      if (id) {
        const row = examList.find((r) => String(r._id) === String(id));
        if (row) {
          setFormData({
            examCode: row.examCode || "",
            examName: row.examName || "",
            status: row.status || "ACTIVE",
          });
          setEditId(row._id);
        }
      }
    } catch {
      toast.error("FAILED TO LOAD DATABASE");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExams();
  }, [id]);

  useEffect(() => {
    if (location.pathname === "/dashboard/exam-master") {
      setViewMode("form"); // Default to form view when clicking sidebar link directly
    }
  }, [location.pathname]);

  const resetForm = () => {
    setEditId(null);
    setFormData({
      examCode: "[AUTO GENERATED]",
      examName: "",
      status: "ACTIVE",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.examName.trim()) return toast.error("EXAM NAME IS REQUIRED");

    setIsSaving(true);
    try {
      const payload = {
        examName: formData.examName.toUpperCase(),
        status: (formData.status || "ACTIVE").toUpperCase(),
      };
      if (editId) {
        payload.id = editId;
        payload.examCode = formData.examCode;
      }

      const res = await upsertExam(payload);
      if (res.data.success || (res.status >= 200 && res.status < 300)) {
        toast.success(editId ? "RECORD UPDATED" : "RECORD SAVED");
        resetForm();
        fetchExams();
        setViewMode("form");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "SAVE FAILED - CHECK SERVER");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({
      examCode: item.examCode || "",
      examName: item.examName || "",
      status: item.status || "ACTIVE",
    });
    setViewMode("form");
  };

  const handleDelete = async (recordId) => {
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) {
      try {
        await deleteExam(recordId);
        toast.success("DELETED SUCCESSFULLY");
        fetchExams();
      } catch {
        toast.error("DELETE FAILED");
      }
    }
  };

  const filteredData = exams.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };

    return (
      startsWithFirstLetter(item.examName) ||
      startsWithFirstLetter(item.examCode) ||
      startsWithFirstLetter(item.status || "ACTIVE")
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar`}>
      {viewMode === "form" ? (
        <div>
          {/* HEADER CARD FOR FORM VIEW */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <BookOpen size={13} />
                </div>
                <h1 className={masterHeadingMain}>{editId ? "Edit Exam" : "Exam Form"}</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("list");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={`${masterFormCard} flex flex-col gap-1 mt-3`}>
            {/* ROW 1: INPUT FIELDS */}
            <div className="flex flex-col md:flex-row items-center gap-3 w-full">
              {/* Field 1: Exam Name */}
              <div className={`${masterFormFieldGroup} flex-1`}>
                <label className={masterInputLabel}>Exam Name</label>
                <input
                  autoFocus
                  type="text"
                  value={formData.examName}
                  onChange={(e) =>
                    setFormData({ ...formData, examName: e.target.value.toUpperCase() })
                  }
                  placeholder="ENTER EXAM NAME"
                  className={masterInput}
                />
              </div>

              {/* Field 2: Status */}
              <div className={`${masterFormFieldGroup} w-full md:w-36`}>
                <label className={masterInputLabel}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className={masterSelect}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            {/* ROW 2: BUTTONS (LEFT BACK, RIGHT SAVE) */}
            <div className="flex items-center justify-between w-full pt-0.5">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setViewMode("list");
                }}
                className={`${masterBtnBack} flex items-center justify-center gap-1.5 h-[26px]`}
              >
                <ArrowLeft size={14} />
                <span>BACK</span>
              </button>

              <div>
                <button
                  disabled={isSaving || loading}
                  type="submit"
                  className={masterFormBtnSubmit}
                >
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editId ? "UPDATE" : "SAVE"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col flex-1 h-full">
          {/* HEADER CARD */}
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <BookOpen size={13} />
                </div>
                <h1 className={masterHeadingMain}>Exam List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchExams}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("form");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW</span>
                </button>
              </div>
            </div>
          </div>

          {/* COMBINED SEARCH & PAGINATION BAR */}
          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH EXAM CODE, NAME, OR STATUS BY FIRST LETTER..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          {/* TABLE */}
          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className={masterTable}>
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>EXAM NAME</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className={masterTdEmpty}>
                        Syncing Database...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item,index) => (
                      <tr key={item._id} className={masterTableRow}>
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>{item.examName}</td>
                        <td className={masterTdStatus}>
                          <span
                            className={
                              item.status === "INACTIVE"
                                ? masterBadgeInactive
                                : masterBadgeLive
                            }
                          >
                            {item.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className={masterActionBtnEdit}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item._id)}
                              className={masterActionBtnDelete}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamMaster;